import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { Link } from "react-router-dom";
import DashboardIcon from '@mui/icons-material/Dashboard';
import RateReviewIcon from '@mui/icons-material/RateReview';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useLocation } from "react-router-dom";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { ReactComponent as Logo } from '../assets/logo.svg'
import Box from '@mui/material/Box'
import Menu from '@mui/material/Menu';
import { useState, createContext } from 'react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import InsightsIcon from '@mui/icons-material/Insights';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

// Create Navigation Context
export const NavigationContext = createContext();

const Layout = ({ children, userRole }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));
  // Simplified logic: Show sidebar only on large screens
  const showSidebar = isLarge;
  const drawerWidth = 240;

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const NavigationList = () => (
    <List 
      data-demo-target="sidebar-nav"
      sx={{
      px: { xs: 1, lg: 2 },
      '& .MuiListItemButton-root': {
        borderRadius: '12px',
        mb: 1,
        height: '44px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
          transform: 'translateX(4px)'
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)'
          }
        }
      },
      '& .MuiListItemIcon-root': {
        minWidth: 48,
        color: '#546E7A',
        transition: 'color 0.2s ease-in-out',
        '.Mui-selected &': {
          color: '#1976d2'
        }
      },
      '& .MuiListItemText-root': {
        margin: 0,
        '& .MuiTypography-root': {
          fontWeight: 500,
          fontSize: '0.95rem',
          color: '#546E7A',
          transition: 'color 0.2s ease-in-out',
          '.Mui-selected &': {
            color: '#1976d2',
            fontWeight: 600
          }
        }
      }
    }}>
      <ListItemButton 
        component={Link} 
        to="/" 
        selected={isActive('/')}
        onClick={handleMenuClose}
      >
        <ListItemIcon>
          <DashboardIcon />
        </ListItemIcon>
        <ListItemText 
          primary="Dashboard" 
          sx={{ opacity: 1, transition: 'opacity 0.2s ease-in-out' }}
        />
      </ListItemButton>

      <ListItemButton 
        component={Link} 
        to="/reviews" 
        selected={isActive('/reviews')}
        onClick={handleMenuClose}
      >
        <ListItemIcon>
          <RateReviewIcon />
        </ListItemIcon>
        <ListItemText 
          primary="Reviews" 
          sx={{ opacity: 1, transition: 'opacity 0.2s ease-in-out' }}
        />
      </ListItemButton>

      {userRole === 'admin' && (
        <ListItemButton 
          component={Link} 
          to="/sales-data" 
          selected={isActive('/sales-data')}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <AttachMoneyIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Sales Data" 
            sx={{ opacity: 1, transition: 'opacity 0.2s ease-in-out' }}
          />
        </ListItemButton>
      )}

      <ListItemButton 
        component={Link} 
        to="/timeline"
        selected={isActive('/timeline')}
        onClick={handleMenuClose}
      >
        <ListItemIcon>
          <CalendarMonthIcon />
        </ListItemIcon>
        <ListItemText 
          primary="Timeline"
          sx={{ opacity: 1, transition: 'opacity 0.2s ease-in-out' }}
        />
      </ListItemButton>

      {/* Categories */}
      <ListItemButton 
        component={Link} 
        to="/settings/custom-categories" 
        selected={isActive('/settings/custom-categories')}
        onClick={handleMenuClose}
      >
        <ListItemIcon>
          <PlaylistAddCheckIcon />
        </ListItemIcon>
        <ListItemText 
          primary="Categories" 
          sx={{ opacity: 1, transition: 'opacity 0.2s ease-in-out' }}
        />
      </ListItemButton>

      <ListItemButton 
        component={Link} 
        to="/businesses" 
        selected={isActive('/businesses')}
        onClick={handleMenuClose}
      >
        <ListItemIcon>
          <StorefrontIcon />
        </ListItemIcon>
        <ListItemText 
          primary="Businesses" 
          sx={{ opacity: 1, transition: 'opacity 0.2s ease-in-out' }}
        />
      </ListItemButton>

      {userRole === 'admin' && (
          <ListItemButton 
            component={Link} 
            to="/admin" 
            selected={isActive('/admin')}
            onClick={handleMenuClose}
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Admin" 
              sx={{ opacity: 1, transition: 'opacity 0.2s ease-in-out' }}
            />
          </ListItemButton>
      )}

    </List>
  );

  // Create context value with navigation state and handlers
  const navigationContextValue = {
    showSidebar,
    handleMenuClick,
    isMenuOpen,
    menuAnchorEl,
    handleMenuClose
  };

  return (
    <NavigationContext.Provider value={navigationContextValue}>
      <div style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Sidebar Drawer - Only show on large screens */}
        {showSidebar && (
          <Drawer
            variant="permanent"
            data-filter-exempt="true"
            sx={{
              '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
                overflowX: 'hidden',
                position: 'relative',
                background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fe 100%)',
                borderRight: '1px solid rgba(25, 118, 210, 0.08)',
                boxShadow: '4px 0 30px rgba(0, 0, 0, 0.03)',
                transition: 'width 0.3s ease-in-out'
              },
            }}
          >
            <Box sx={{
              width: 1,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 1,
              mt: 2,
              mb: 0
            }}>
              <Logo style={{
                width: '100%',
                height: 'auto',
                maxWidth: 100
              }} />
            </Box>

            <NavigationList />
          </Drawer>
        )}

        {/* Navigation Menu (Dropdown) */}
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          data-filter-exempt="true"
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '12px',
              boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
              border: '1px solid rgba(25, 118, 210, 0.1)',
              minWidth: '240px'
            },
            'data-filter-exempt': 'true'
          }}
        >
          <NavigationList />
        </Menu>

        {/* Main Content */}
        <Box
          sx={{
            flexGrow: 1,
            height: '100vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
            position: 'relative',
            maxHeight: '100vh' // Enforce maximum height constraint
          }}
        >
          {children}
        </Box>
      </div>
    </NavigationContext.Provider>
  );
};

export default Layout;
